import React, { createContext, useContext, useState, useCallback } from "react";
import api from "../utils/axiosInstance";

export const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Wrap fetchSubscription in useCallback to prevent infinite loops
  const fetchSubscription = useCallback(async (carrierId) => {
    if (!carrierId) return null;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/subscribed/by-carrier/${carrierId}`);

      // The API returns the subscription data with success flag
      const subscriptionData = response.data;

      setSubscription(subscriptionData);
      // Also store in localStorage for persistence
      localStorage.setItem(
        "carrierSubscription",
        JSON.stringify(subscriptionData)
      );
      return subscriptionData;
    } catch (err) {
      console.error("Error fetching subscription:", err);
      setError(err.response?.data?.message || "Failed to fetch subscription");
      setSubscription(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSubscription = useCallback(() => {
    setSubscription(null);
    setError(null);
    localStorage.removeItem("carrierSubscription");
  }, []);

  // Load subscription from localStorage on initial load
  const loadSubscriptionFromStorage = useCallback(() => {
    try {
      const savedSubscription = localStorage.getItem("carrierSubscription");
      if (savedSubscription) {
        setSubscription(JSON.parse(savedSubscription));
      }
    } catch (error) {
      console.error("Error loading subscription from localStorage:", error);
      localStorage.removeItem("carrierSubscription");
    }
  }, []);

  // Helper function to check if subscription is valid (active and not expired)
  const isSubscriptionValid = useCallback(() => {
    if (!subscription) return false;

    // Check if status is active
    const status = subscription.status?.toLowerCase();
    const isActive = status === "active";

    if (!isActive) return false;

    // Check if subscription has expired
    if (subscription.currentPeriodEnd) {
      const expiryDate = new Date(subscription.currentPeriodEnd);
      const today = new Date();

      // Set both dates to midnight for fair comparison
      expiryDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // If expiry date is today or in the future, it's valid
      return expiryDate >= today;
    }

    // If no expiry date but status is active, consider it valid
    return true;
  }, [subscription]);

  // Helper to get package name
  const getPackageName = useCallback(() => {
    return subscription?.package?.name || "No Package";
  }, [subscription]);

  // Helper to get formatted expiry date
  const getExpiryDate = useCallback(() => {
    if (!subscription?.currentPeriodEnd) return null;
    return new Date(subscription.currentPeriodEnd).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [subscription]);

  // Helper to check how many days until expiry
  const getDaysUntilExpiry = useCallback(() => {
    if (!subscription?.currentPeriodEnd) return null;

    const expiryDate = new Date(subscription.currentPeriodEnd);
    const today = new Date();

    // Set both to midnight
    expiryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }, [subscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        // State
        subscription,
        loading,
        error,

        // Functions
        fetchSubscription,
        clearSubscription,
        loadSubscriptionFromStorage,
        isSubscriptionValid,
        getPackageName,
        getExpiryDate,
        getDaysUntilExpiry,

        // Direct data access (for backward compatibility)
        isSubscriptionActive: subscription?.active || false,
        subscriptionEndDate: subscription?.currentPeriodEnd,
        subscriptionStatus: subscription?.status,
        packageInfo: subscription?.package,
        autoPayment: subscription?.autoPayment,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};
